// WABulkThresholdPanel — v1 (2026-09)
// Owner-editable "max recipients without approval" threshold. Any survey
// blast whose recipient count EXCEEDS this value requires explicit owner/
// delegate approval. Reads/writes /api/whatsapp/bulk-approval-threshold;
// the value is applied LIVE to the next blast request (no restart/redeploy).
// Does NOT touch the daily quota, safety margin, or master kill switch.
import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatKSADateTime } from "../lib/ksaTime";

const fmt = (iso) => {
  try { return formatKSADateTime(new Date(iso), { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || ""; }
};

export default function WABulkThresholdPanel() {
  const [value, setValue] = useState("");
  const [meta, setMeta] = useState({ default: 10, set_by: null, set_at: null, daily_allowance: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/bulk-approval-threshold");
      setValue(String(data.value));
      setMeta({
        default: data.default, set_by: data.set_by, set_at: data.set_at,
        daily_allowance: data.daily_allowance,
        daily_quota: data.daily_quota, safety_margin: data.safety_margin,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحميل الإعداد");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(value, 10);
    if (!Number.isInteger(n) || n < 1 || n > 100000) {
      toast.error("أدخل رقمًا صحيحًا بين 1 و100000");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/whatsapp/bulk-approval-threshold", { value: n });
      setMeta((m) => ({ ...m, set_by: data.set_by, set_at: data.set_at }));
      toast.success(`✅ حُفظ فورًا — أي دفعة أكبر من ${data.value} مستلم تتطلب موافقة الآن.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحفظ");
    } finally { setSaving(false); }
  };

  // Live soft-cap + warning derived from the CURRENT daily allowance
  // (wa_daily_quota − wa_safety_margin). UI-only: never blocks saving.
  const allowance = meta.daily_allowance;
  const nVal = parseInt(value, 10);
  const overHalf = Number.isInteger(nVal) && allowance != null && nVal > Math.floor(allowance * 0.5);

  return (
    <div className="card" data-testid="wa-bulk-threshold-panel" dir="rtl" style={{ padding: 18, maxWidth: 620 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: "var(--brown-deep)", marginBottom: 4 }}>
        🚦 الحد الأقصى للإرسال بلا موافقة
      </div>
      <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.9, marginBottom: 16 }}>
        أي دفعة إرسال (بث استبيان) عدد مستلميها <b>أكبر</b> من هذا الرقم تتطلب موافقة صريحة
        من المالك أو مستخدم مفوَّض. الدفعات بحجم هذا الرقم أو أقل تُرسَل تلقائيًا على دفعات
        فرعية آمنة. هذا الإعداد يتحكم فقط بعتبة «متى تُطلب موافقة» — ولا يمسّ الحصة اليومية
        أو حدود الأمان أو مفتاح الإيقاف الرئيسي.
      </div>

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 13 }}>جارٍ التحميل…</div>
      ) : (
        <>
          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>
            الحد الأقصى (عدد المستلمين)
          </label>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              data-testid="wa-bulk-threshold-input"
              type="number"
              min={1}
              max={allowance || 100000}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{
                width: 160, minHeight: 44, borderRadius: 10, padding: "8px 12px",
                fontSize: 15, fontWeight: 700, textAlign: "center",
                border: "1px solid rgba(140,90,30,0.35)", background: "#fff", color: "var(--brown-deep)",
              }}
            />
            <button
              data-testid="wa-bulk-threshold-save"
              className="btn btn-primary"
              disabled={saving}
              onClick={save}
              style={{ minHeight: 44, padding: "8px 22px", fontWeight: 800 }}
            >
              {saving ? "⏳ جارٍ الحفظ…" : "حفظ"}
            </button>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>الافتراضي: {meta.default}</span>
          </div>

          {allowance != null && (
            <div data-testid="wa-bulk-threshold-hint" style={{ fontSize: 12, color: "var(--muted)", marginTop: 8, lineHeight: 1.8 }}>
              لا معنى لتجاوز المتاح اليومي الحالي (<b>{allowance}</b>) — أي دفعة تحتاجه فعلاً محجوبة بالحصة أصلاً.
            </div>
          )}

          {overHalf && (
            <div data-testid="wa-bulk-threshold-warning" style={{
              fontSize: 12.5, marginTop: 12, padding: "10px 12px", borderRadius: 10,
              background: "rgba(245,158,11,0.12)", border: "1px solid #f59e0b",
              color: "#b45309", lineHeight: 1.8, fontWeight: 600,
            }}>
              ⚠️ هذا الرقم يقترب من إلغاء المراجعة البشرية لأغلب دفعاتك اليومية — تأكد أنك تقصد هذا.
            </div>
          )}

          {meta.set_by && (
            <div data-testid="wa-bulk-threshold-meta" style={{ fontSize: 12, color: "var(--muted)", marginTop: 14 }}>
              آخر تعديل بواسطة <b>{meta.set_by}</b> · {fmt(meta.set_at)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
